import React, { useMemo } from 'react';

import { HStack, Text } from '@rtkwlf/fenrir-react';
import type { ScheduleRow } from '@rtkwlf/rendall';
import type { ColumnDef, Row } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { DateTime } from 'ae-components/dateTime/DateTime';

import { LongTextTooltip } from '../../../../../../../../../../../components/long-text-tooltip/long-text-tooltip';
import { ScanTargets } from '../../../../../../../../../../scan-schedules/components/scan-targets';
import { ScannerStatus } from '../../../../../../../../../components/scanner-config/scanners-status';
import { DeleteScheduleButton } from '../../delete-schedule-button/delete-schedule-button';
import { EditScheduleButton } from '../../edit-schedule-button/edit-schedule-button';
import { ScanningBadge } from '../../scanning-badge/scanning-badge';

interface ScanningScheduleRow extends ScheduleRow {
  scanPriority: string;
  targetsStr: string;
  targetScanningStatus: string;
  tzNextScan: string;
}

const columnHelper = createColumnHelper<ScanningScheduleRow>();

export const useScanningScheduleColumns = (): ColumnDef<
  ScanningScheduleRow,
  any
>[] => {
  const { t } = useTranslation('telemetryScanSchedules');

  return useMemo(
    () => [
      columnHelper.accessor('name', {
        cell: ({ getValue }) => <LongTextTooltip value={getValue()} />,
        enableSorting: true,
        header: t('name'),
      }),
      columnHelper.accessor('description', {
        cell: ({ getValue }) => <LongTextTooltip value={getValue()} />,
        enableSorting: true,
        header: t('description'),
        meta: {
          width: 'auto',
        },
      }),
      columnHelper.accessor('targetScanningStatus', {
        cell: ({ row }) => (
          <ScanningBadge scanning={row.original.scanningEnabled} />
        ),
        enableSorting: true,
        header: t('scanning'),
      }),
      columnHelper.accessor('status', {
        cell: ({ getValue }) => <ScannerStatus value={getValue()} />,
        enableSorting: true,
        header: t('status'),
      }),
      columnHelper.accessor('frequency', {
        cell: ({ getValue }) => (
          <Text whiteSpace="nowrap" styledAs="body3">
            {t(`telemetryScanSchedules:frequencyTypes:${getValue()}`)}
          </Text>
        ),
        enableSorting: true,
        header: t('frequency'),
      }),
      columnHelper.accessor('lastScan', {
        cell: ({ getValue }) => {
          return dayjs(getValue()).isValid() ? (
            <DateTime value={getValue().toString()} />
          ) : (
            t('noLastScan')
          );
        },
        enableSorting: true,
        header: t('lastScan'),
        meta: {
          width: '20',
        },
      }),
      columnHelper.accessor('tzNextScan', {
        cell: ({ row }) => {
          const { nextScan } = row.original;

          return dayjs(nextScan).isValid() ? (
            <DateTime value={nextScan.toString()} />
          ) : (
            t('scannerScanSchedule.noScan')
          );
        },
        enableSorting: true,
        header: t('nextScan'),
        meta: {
          width: '20',
        },
      }),
      columnHelper.accessor('targetsStr', {
        cell: ({ row }) => (
          <ScanTargets
            targets={row.original.targets}
            source={row.original.source}
          />
        ),
        header: t('targets'),
        meta: {
          width: '20',
        },
      }),
      {
        cell: ({ row }: { row: Row<ScanningScheduleRow> }) => {
          return (
            <HStack gap="xsmall">
              <EditScheduleButton scanScheduleRow={row.original} />
              <DeleteScheduleButton scanScheduleRow={row.original} />
            </HStack>
          );
        },
        header: undefined,
        id: 'actions',
      },
    ],
    [t],
  );
};
